import styled from 'styled-components';
import { PRIMARY_COLOR, BORDER_COLOR, BACKGROUND_COLOR } from './constants';

/**
 * EvidenceIconSection - Container for evidence icons
 * Flexible container that arranges icons in a row
 */
export const EvidenceIconSection = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ gap }) => gap || '8px'};
  min-width: 0;
  flex-wrap: wrap;
`;

/**
 * EvidenceIcon - Individual image/audio evidence button
 * Clickable icon with border, supports custom sizing
 * Portrait rectangle (height > width)
 */
export const EvidenceIcon = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${({ width }) => width || '40px'};
  height: ${({ height }) => height || '60px'};
  border: 1px solid ${BORDER_COLOR};
  border-radius: ${({ borderRadius }) => borderRadius || '8px'};
  padding: 0;
  cursor: pointer;
  background: ${BACKGROUND_COLOR};
  flex-shrink: 0;
  transition: all 0.2s ease;

  img {
    width: 100%;
    height: 100%;
    border-radius: ${({ borderRadius }) => borderRadius || '6px'};
    object-fit: cover;
  }

  &:hover {
    border-color: ${PRIMARY_COLOR};
    box-shadow: 0 2px 8px rgba(34, 102, 239, 0.1);
  }

  &:active {
    transform: scale(0.95);
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.4;
  }

  span {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
  }
`;

/**
 * MoreBadge - "+N" badge for remaining evidence items
 * Text-only badge matching evidence height
 */
export const MoreBadge = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: auto;
  min-width: 24px;
  height: ${({ height }) => height || '60px'};
  border: none;
  padding: 0 4px;
  cursor: pointer;
  background: transparent;
  flex-shrink: 0;
  transition: all 0.2s ease;
  font-size: 12px;
  font-weight: 600;
  color: ${PRIMARY_COLOR};

  &:hover {
    opacity: 0.8;
  }

  &:active {
    transform: scale(0.95);
  }
`;

/**
 * SkeletonIconLoader - Animated skeleton loading state for evidence icon
 */
export const SkeletonIconLoader = styled.div`
  height: ${({ height }) => height || '45px'};
  width: ${({ width }) => width || '45px '};
  border-radius: ${({ borderRadius }) => borderRadius || '8px'};
  background: linear-gradient(
    90deg,
    #eef3fb 0%,
    #dae4f4 50%,
    #eef3fb 100%
  );
  background-size: 200% 100%;
  animation: shimmer 1.4s infinite;
  flex-shrink: 0;

  @keyframes shimmer {
    0% {
      background-position: 200% 0;
    }
    100% {
      background-position: -200% 0;
    }
  }
`;

/**
 * EvidenceSkeletonContainer - Container for skeleton loaders
 */
export const EvidenceSkeletonContainer = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ gap }) => gap || '8px'};
  min-width: 0;
`;

/**
 * AudioDurationText - Text displayed below audio icon
 */
export const AudioDurationText = styled.span`
  font-size: 10px;
  color: ${PRIMARY_COLOR};
  font-weight: 600;
  padding: 2px 0;
  white-space: nowrap;
  text-align: center;
`;

/**
 * EmptyStateText - Placeholder text when no evidence available
 */
export const EmptyStateText = styled.span`
  font-size: 12px;
  color: #999;
  font-weight: 500;
`;

/**
 * AudioEvidenceWrapper - Container for audio icon and duration
 * Has same height as image icon, stacks icon and duration vertically inside
 */
export const AudioEvidenceWrapper = styled.button`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: ${({ width }) => width || '40px'};
  height: ${({ height }) => height || '60px'};
  border: 1px solid ${BORDER_COLOR};
  border-radius: ${({ borderRadius }) => borderRadius || '8px'};
  padding: 4px 0;
  cursor: pointer;
  background: ${BACKGROUND_COLOR};
  flex-shrink: 0;
  transition: all 0.2s ease;
  gap: 2px;

  img {
    width: 20px;
    height: 20px;
    flex-shrink: 0;
  }

  &:hover {
    border-color: ${PRIMARY_COLOR};
    box-shadow: 0 2px 8px rgba(34, 102, 239, 0.1);
  }

  &:active {
    transform: scale(0.95);
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.4;
  }
`;
